import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, BarChart3, Download, FileText, Play, Presentation, ChevronLeft, ChevronRight, Star, Flag, Users, DollarSign, Target, Lightbulb, AlertTriangle, CheckCircle, Award, Globe, Settings, Heart, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';

export const PresentationsPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [companyName, setCompanyName] = useState('ACME Company');
  const [date, setDate] = useState(new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));

  const totalSlides = 14;

  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) setCurrentSlide(currentSlide + 1);
  };

  const prevSlide = () => {
    if (currentSlide > 0) setCurrentSlide(currentSlide - 1);
  };

  const downloads = [
    { id: 1, name: 'Financial Model Template.xlsx', type: 'Excel', size: '2.4 MB', icon: FileText, description: 'Complete 3-statement financial model template' },
    { id: 2, name: 'Board Deck Template.pptx', type: 'PowerPoint', size: '5.1 MB', icon: Presentation, description: 'Professional board presentation template' },
    { id: 3, name: 'Startup Metrics Guide.pdf', type: 'PDF', size: '1.8 MB', icon: FileText, description: 'Comprehensive guide to SaaS metrics and KPIs' },
  ];

  const MetricCard = ({ value, label, change, positive = true }) => (
    <div className="bg-[#1A2332] rounded-lg p-3 sm:p-4 text-center border border-[#C9A64A]/20">
      <div className="text-xl sm:text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-xs sm:text-sm text-gray-400 mb-1">{label}</div>
      {change && (
        <div className={`text-xs font-semibold ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
          {positive ? '↑' : '↓'} {change}
        </div>
      )}
    </div>
  );

  const ListItem = ({ icon: Icon, text, type = 'check' }) => (
    <div className="flex items-start gap-2 mb-2">
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${type === 'check' ? 'text-emerald-400' : type === 'warning' ? 'text-amber-400' : 'text-[#C9A64A]'}`} />
      <span className="text-gray-300 text-sm">{text}</span>
    </div>
  );

  const SectionCard = ({ title, icon: Icon, children, className = '' }) => (
    <div className={`bg-[#1A2332] rounded-xl p-4 border border-[#C9A64A]/10 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-5 w-5 text-[#C9A64A]" />
        <h3 className="text-base font-bold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );

  const StatusBadge = ({ status }) => {
    const styles = {
      completed: 'bg-emerald-500/20 text-emerald-400',
      'in-progress': 'bg-blue-500/20 text-blue-400',
      'not-started': 'bg-gray-500/20 text-gray-400',
      high: 'bg-red-500/20 text-red-400',
      medium: 'bg-amber-500/20 text-amber-400',
      low: 'bg-emerald-500/20 text-emerald-400',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || styles['not-started']}`}>
        {status.replace('-', ' ').toUpperCase()}
      </span>
    );
  };

  const renderSlide = () => {
    switch (currentSlide) {
      case 0: // Cover
        return (
          <div className="h-full flex flex-col items-center justify-center p-6 sm:p-12 text-center relative min-h-[400px]">
            <div className="absolute inset-0 opacity-10 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&q=85)' }} />
            <div className="relative z-10 space-y-4 sm:space-y-6">
              <div className="text-base sm:text-xl font-bold tracking-[0.2em] text-[#C9A64A] uppercase">{companyName}</div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white">Board Deck</h1>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-light text-gray-300">Quarterly Review</h2>
              <div className="inline-block px-5 py-3 border-2 border-white/30 rounded-lg text-base sm:text-lg text-white">{date}</div>
            </div>
          </div>
        );

      case 1: // Executive Summary
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">Executive Summary</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Quarter Highlights" icon={Star}>
                <ListItem icon={CheckCircle} text="Revenue growth of 15% exceeding forecast" />
                <ListItem icon={CheckCircle} text="Successful product launch in new market" />
                <ListItem icon={CheckCircle} text="Operational efficiency improved by 12%" />
                <ListItem icon={CheckCircle} text="Strategic partnership with industry leader" />
              </SectionCard>
              <SectionCard title="Key Performance Metrics" icon={TrendingUp}>
                <div className="grid grid-cols-3 gap-2">
                  <MetricCard value="15%" label="Revenue Growth" />
                  <MetricCard value="23%" label="Profit Margin" />
                  <MetricCard value="92%" label="Customer Sat." />
                </div>
              </SectionCard>
              <SectionCard title="Strategic Focus Areas" icon={Lightbulb}>
                <ListItem icon={ChevronRight} text="Digital Transformation Initiative" type="arrow" />
                <ListItem icon={ChevronRight} text="Market Expansion in APAC Region" type="arrow" />
                <ListItem icon={ChevronRight} text="Product Innovation Pipeline" type="arrow" />
              </SectionCard>
              <SectionCard title="Executive Message" icon={MessageSquare}>
                <p className="text-gray-300 italic text-sm leading-relaxed mb-2">&quot;This quarter marks a significant milestone. Our team has demonstrated exceptional resilience and innovation.&quot;</p>
                <p className="text-[#C9A64A] font-semibold text-sm">— CEO, {companyName}</p>
              </SectionCard>
            </div>
          </div>
        );

      case 2: // Agenda
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">Meeting Agenda</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: Star, title: 'Executive Summary', duration: '5 min', desc: 'Quarter highlights' },
                { icon: BarChart3, title: 'Company Overview', duration: '5 min', desc: 'Mission & vision' },
                { icon: DollarSign, title: 'Financial Summary', duration: '15 min', desc: 'Budget vs actuals' },
                { icon: Users, title: 'Dept Updates', duration: '20 min', desc: 'Team performance' },
                { icon: Lightbulb, title: 'Strategic Init.', duration: '10 min', desc: 'Project status' },
                { icon: AlertTriangle, title: 'Risk Assessment', duration: '10 min', desc: 'Top risks' },
                { icon: Target, title: 'Action Items', duration: '5 min', desc: 'Next steps' },
                { icon: MessageSquare, title: 'Q&A', duration: '15 min', desc: 'Discussion' },
              ].map((item, i) => (
                <div key={i} className="bg-[#1A2332] rounded-lg p-3 border border-[#C9A64A]/10">
                  <item.icon className="h-4 w-4 text-[#C9A64A] mb-2" />
                  <h4 className="font-semibold text-white text-sm">{item.title}</h4>
                  <div className="text-[#C9A64A] font-bold text-sm">{item.duration}</div>
                  <div className="text-xs text-gray-400">{item.desc}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-[#C9A64A]/20 rounded-lg p-3 text-center border border-[#C9A64A]/30">
              <span className="text-[#C9A64A] font-bold text-lg">Total: 85 minutes</span>
            </div>
          </div>
        );

      case 3: // Company Overview
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">Company Overview</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Company History" icon={Flag}>
                {['2010 - Founded by industry pioneers', '2014 - First major product launch', '2017 - International expansion', '2020 - Strategic acquisition of TechCorp', '2023 - Industry leader in innovation'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 mb-2">
                    <span className="text-[#C9A64A] font-bold text-sm">{item.split(' - ')[0]}</span>
                    <span className="text-gray-300 text-sm">{item.split(' - ')[1]}</span>
                  </div>
                ))}
              </SectionCard>
              <SectionCard title="Mission & Vision" icon={Target}>
                <div className="space-y-3">
                  <div><span className="text-[#C9A64A] font-semibold">Mission:</span><p className="text-gray-300 text-sm mt-1">Deliver innovative solutions that transform industries.</p></div>
                  <div><span className="text-[#C9A64A] font-semibold">Vision:</span><p className="text-gray-300 text-sm mt-1">Be the global leader setting standards for excellence.</p></div>
                </div>
              </SectionCard>
              <SectionCard title="Core Values" icon={Star} className="lg:col-span-2">
                <div className="flex flex-wrap gap-2">
                  {['Innovation', 'Integrity', 'Customer Focus', 'Excellence', 'Sustainability', 'Collaboration'].map((v, i) => (
                    <span key={i} className="bg-[#0F1A2A] rounded-lg px-3 py-2 text-white font-semibold text-sm border border-[#C9A64A]/20">{v}</span>
                  ))}
                </div>
              </SectionCard>
            </div>
          </div>
        );

      case 4: // Financial Summary
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">Financial Summary</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Revenue & Profit" icon={TrendingUp}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="$15.2M" label="Revenue" change="15% vs. budget" positive={true} />
                  <MetricCard value="$3.5M" label="Gross Profit" change="12% vs. budget" positive={true} />
                </div>
              </SectionCard>
              <SectionCard title="Balance Sheet" icon={DollarSign}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="$42.8M" label="Total Assets" />
                  <MetricCard value="$18.5M" label="Total Liabilities" />
                </div>
              </SectionCard>
              <SectionCard title="Cash Flow" icon={BarChart3}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="$4.2M" label="Operating CF" change="8% vs. last Q" positive={true} />
                  <MetricCard value="$1.8M" label="Free CF" />
                </div>
              </SectionCard>
              <SectionCard title="Health Indicators" icon={Target}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <MetricCard value="23%" label="Profit Margin" />
                  <MetricCard value="1.8" label="Current Ratio" />
                  <MetricCard value="0.43" label="D/E Ratio" />
                  <MetricCard value="18%" label="ROE" />
                </div>
              </SectionCard>
            </div>
          </div>
        );

      case 5: // KPI Dashboard
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">KPI Dashboard</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Revenue Growth" icon={TrendingUp}>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <MetricCard value="15%" label="YoY Growth" change="+3% vs target" positive={true} />
                  <MetricCard value="$15.2M" label="Q3 Revenue" change="+8% vs Q2" positive={true} />
                </div>
                <div className="flex items-center gap-2 text-emerald-400 text-xs"><CheckCircle className="h-3 w-3" /><span>On track for annual target</span></div>
              </SectionCard>
              <SectionCard title="Customer Metrics" icon={Users}>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <MetricCard value="92%" label="Satisfaction" change="+2% vs target" positive={true} />
                  <MetricCard value="4.8%" label="Churn Rate" change="-0.5%" positive={true} />
                </div>
                <div className="flex items-center gap-2 text-emerald-400 text-xs"><CheckCircle className="h-3 w-3" /><span>Exceeding retention goals</span></div>
              </SectionCard>
              <SectionCard title="Operations" icon={Settings}>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <MetricCard value="87%" label="Capacity" />
                  <MetricCard value="3.2" label="Cycle Time" change="-0.5 days" positive={true} />
                </div>
                <div className="flex items-center gap-2 text-amber-400 text-xs"><AlertTriangle className="h-3 w-3" /><span>Q4 capacity constraints expected</span></div>
              </SectionCard>
              <SectionCard title="Employee" icon={Heart}>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <MetricCard value="94%" label="Engagement" change="+4%" positive={true} />
                  <MetricCard value="8.5%" label="Turnover" change="+1.5%" positive={false} />
                </div>
                <div className="flex items-center gap-2 text-amber-400 text-xs"><AlertTriangle className="h-3 w-3" /><span>Retention initiatives in progress</span></div>
              </SectionCard>
            </div>
          </div>
        );

      case 6: // Marketing
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">Marketing Update</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Campaign Performance" icon={Target}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="3.2x" label="Avg. ROI" change="+0.5 vs last Q" positive={true} />
                  <MetricCard value="87%" label="Target Achievement" />
                </div>
              </SectionCard>
              <SectionCard title="Lead Generation" icon={Users}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="2,847" label="Total Leads" change="+18%" positive={true} />
                  <MetricCard value="24%" label="Conversion" change="+3%" positive={true} />
                </div>
              </SectionCard>
              <SectionCard title="Brand Awareness" icon={Globe}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="68%" label="Recognition" change="+5%" positive={true} />
                  <MetricCard value="4.2/5" label="Sentiment" />
                </div>
              </SectionCard>
              <SectionCard title="Achievements & Challenges" icon={Lightbulb}>
                <ListItem icon={CheckCircle} text="Successful product launch campaign" />
                <ListItem icon={CheckCircle} text="25% market share in new market" />
                <ListItem icon={AlertTriangle} text="Increasing acquisition costs" type="warning" />
              </SectionCard>
            </div>
          </div>
        );

      case 7: // Sales
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">Sales Update</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Sales Pipeline" icon={TrendingUp}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="$42.5M" label="Total Pipeline" change="+18%" positive={true} />
                  <MetricCard value="28%" label="Conversion" />
                </div>
              </SectionCard>
              <SectionCard title="Deal Closures" icon={Award}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="$15.2M" label="Q3 Revenue" change="+15%" positive={true} />
                  <MetricCard value="47" label="Deals Closed" change="+8" positive={true} />
                </div>
              </SectionCard>
              <SectionCard title="Regional Performance" icon={Globe}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="N. America" label="Top Region" change="+22%" positive={true} />
                  <MetricCard value="APAC" label="Fastest Growing" change="+35%" positive={true} />
                </div>
              </SectionCard>
              <SectionCard title="Top Performers" icon={Star}>
                <ListItem icon={Award} text="John Smith - $3.2M in Q3 sales" type="arrow" />
                <ListItem icon={Award} text="Sarah Johnson - 28% above target" type="arrow" />
                <ListItem icon={TrendingUp} text="Enterprise - 42% YoY growth" type="arrow" />
              </SectionCard>
            </div>
          </div>
        );

      case 8: // Operations
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">Operations Update</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Efficiency" icon={Settings}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="87%" label="Capacity" change="+5%" positive={true} />
                  <MetricCard value="96.2%" label="On-Time Delivery" change="+2.1%" positive={true} />
                </div>
              </SectionCard>
              <SectionCard title="Process Improvements" icon={TrendingUp}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="18%" label="Cost Reduction" change="$2.3M saved" positive={true} />
                  <MetricCard value="3.2" label="Cycle Time" change="-0.5 days" positive={true} />
                </div>
              </SectionCard>
              <SectionCard title="Supply Chain" icon={Globe}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="24" label="Suppliers" change="-6 vs last year" positive={true} />
                  <MetricCard value="98.5%" label="Reliability" />
                </div>
              </SectionCard>
              <SectionCard title="Initiatives" icon={Lightbulb}>
                <ListItem icon={TrendingUp} text="40% of processes automated" type="arrow" />
                <ListItem icon={Heart} text="15% carbon footprint reduction" type="arrow" />
                <ListItem icon={AlertTriangle} text="Supply constraints expected Q4" type="warning" />
              </SectionCard>
            </div>
          </div>
        );

      case 9: // HR
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">HR Update</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Headcount & Hiring" icon={Users}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="542" label="Total Headcount" change="+8%" positive={true} />
                  <MetricCard value="38" label="New Hires" change="+12 vs target" positive={true} />
                </div>
              </SectionCard>
              <SectionCard title="Retention" icon={TrendingUp}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="8.5%" label="Turnover" change="+1.5%" positive={false} />
                  <MetricCard value="3.2 yrs" label="Avg. Tenure" />
                </div>
              </SectionCard>
              <SectionCard title="Engagement" icon={Heart}>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard value="94%" label="Engagement" change="+4%" positive={true} />
                  <MetricCard value="4.2/5" label="Satisfaction" />
                </div>
              </SectionCard>
              <SectionCard title="Initiatives" icon={Lightbulb}>
                <ListItem icon={Award} text="Leadership development launched" type="arrow" />
                <ListItem icon={Heart} text="Wellness - 85% participation" type="arrow" />
                <ListItem icon={Users} text="D&I program expanded" type="arrow" />
              </SectionCard>
            </div>
          </div>
        );

      case 10: // Strategic Initiatives
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">Strategic Initiatives</h2>
            <div className="bg-[#1A2332] rounded-xl p-4 border border-[#C9A64A]/10 overflow-x-auto">
              <table className="w-full min-w-[500px] text-sm">
                <thead>
                  <tr className="border-b border-[#C9A64A]/20">
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Initiative</th>
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Owner</th>
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Status</th>
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'Digital Transformation', owner: 'J. Smith', status: 'in-progress', progress: '75%' },
                    { name: 'Market Expansion - APAC', owner: 'S. Johnson', status: 'in-progress', progress: '60%' },
                    { name: 'Product Innovation', owner: 'M. Williams', status: 'completed', progress: '100%' },
                    { name: 'Sustainability Program', owner: 'R. Brown', status: 'in-progress', progress: '45%' },
                    { name: 'Customer Experience', owner: 'L. Davis', status: 'not-started', progress: '10%' },
                  ].map((item, i) => (
                    <tr key={i} className="border-b border-[#C9A64A]/10">
                      <td className="py-2 px-3 text-white">{item.name}</td>
                      <td className="py-2 px-3 text-gray-300">{item.owner}</td>
                      <td className="py-2 px-3"><StatusBadge status={item.status} /></td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-[#0F1A2A] rounded-full overflow-hidden">
                            <div className="h-full bg-[#C9A64A]" style={{ width: item.progress }} />
                          </div>
                          <span className="text-gray-300 text-xs">{item.progress}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 11: // Risk Assessment
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">Risk Assessment</h2>
            <div className="bg-[#1A2332] rounded-xl p-4 border border-[#C9A64A]/10 overflow-x-auto">
              <table className="w-full min-w-[500px] text-sm">
                <thead>
                  <tr className="border-b border-[#C9A64A]/20">
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Risk</th>
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Category</th>
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Priority</th>
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Mitigation</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { risk: 'Supply Chain Disruption', category: 'Operations', priority: 'high', mitigation: 'Diversify suppliers' },
                    { risk: 'Talent Retention', category: 'HR', priority: 'high', mitigation: 'Enhanced compensation' },
                    { risk: 'Market Competition', category: 'Strategy', priority: 'medium', mitigation: 'Product differentiation' },
                    { risk: 'Regulatory Changes', category: 'Compliance', priority: 'medium', mitigation: 'Legal monitoring' },
                    { risk: 'Cybersecurity', category: 'IT', priority: 'high', mitigation: 'Security upgrades' },
                  ].map((item, i) => (
                    <tr key={i} className="border-b border-[#C9A64A]/10">
                      <td className="py-2 px-3 text-white">{item.risk}</td>
                      <td className="py-2 px-3 text-gray-300">{item.category}</td>
                      <td className="py-2 px-3"><StatusBadge status={item.priority} /></td>
                      <td className="py-2 px-3 text-gray-300">{item.mitigation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 12: // Action Items
        return (
          <div className="h-full p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">Action Items</h2>
            <div className="bg-[#1A2332] rounded-xl p-4 border border-[#C9A64A]/10 overflow-x-auto">
              <table className="w-full min-w-[500px] text-sm">
                <thead>
                  <tr className="border-b border-[#C9A64A]/20">
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Action</th>
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Owner</th>
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Priority</th>
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Due</th>
                    <th className="text-left py-2 px-3 text-[#C9A64A]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { action: 'Finalize Q4 budget', owner: 'CFO', priority: 'high', due: 'Oct 15', status: 'in-progress' },
                    { action: 'Launch APAC campaign', owner: 'CMO', priority: 'high', due: 'Oct 20', status: 'not-started' },
                    { action: 'Supplier renewals', owner: 'COO', priority: 'medium', due: 'Oct 30', status: 'in-progress' },
                    { action: 'Retention program', owner: 'CHRO', priority: 'high', due: 'Nov 1', status: 'not-started' },
                    { action: 'Security upgrades', owner: 'CTO', priority: 'high', due: 'Nov 15', status: 'in-progress' },
                  ].map((item, i) => (
                    <tr key={i} className="border-b border-[#C9A64A]/10">
                      <td className="py-2 px-3 text-white">{item.action}</td>
                      <td className="py-2 px-3 text-gray-300">{item.owner}</td>
                      <td className="py-2 px-3"><StatusBadge status={item.priority} /></td>
                      <td className="py-2 px-3 text-gray-300">{item.due}</td>
                      <td className="py-2 px-3"><StatusBadge status={item.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 13: // Q&A
        return (
          <div className="h-full flex flex-col items-center justify-center p-6 sm:p-12 text-center relative min-h-[400px]">
            <div className="absolute inset-0 opacity-10 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&q=85)' }} />
            <div className="relative z-10 space-y-6">
              <MessageSquare className="h-12 w-12 text-[#C9A64A] mx-auto" />
              <h1 className="text-3xl sm:text-5xl font-bold text-white">Questions & Discussion</h1>
              <p className="text-base sm:text-lg text-gray-300 max-w-xl mx-auto">Thank you for your attention. We welcome your questions and feedback.</p>
              <div className="pt-6">
                <div className="text-[#C9A64A] font-semibold">{companyName}</div>
                <div className="text-gray-400 text-sm">{date}</div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen hero-gradient">
      <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-4 sm:py-6 bg-[#0F1A2A]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity">
            <div className="relative">
              <TrendingUp className="h-5 w-5 sm:h-7 sm:w-7 text-[#C9A64A]" />
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 text-[#C9A64A] absolute -bottom-1 -right-1" />
            </div>
            <span className="text-lg sm:text-xl font-bold text-white">Virtual<span className="text-[#C9A64A]">CFO</span></span>
          </Link>
          <Link to="/"><Button variant="outline" className="gap-2 border-white/10 text-white hover:bg-white/5 text-sm"><ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">Back</span></Button></Link>
        </div>
      </header>

      <section className="pt-24 sm:pt-32 pb-6 sm:pb-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 mb-4 sm:mb-6 px-4 py-2 bg-[#C9A64A]/20 rounded-full border border-[#C9A64A]/30">
            <Presentation className="h-4 w-4 text-[#C9A64A]" />
            <span className="text-[#C9A64A] text-xs font-bold tracking-widest uppercase">Presentations</span>
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">ACME Board Deck</h1>
          <p className="text-sm sm:text-lg text-gray-300 max-w-2xl mx-auto">Professional 14-slide board presentation template with customizable company name and date.</p>
        </div>
      </section>

      <section className="py-4 sm:py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 sm:mb-6">
            <div className="bg-white/5 p-4 rounded-xl border border-white/10">
              <label className="block text-xs font-bold uppercase text-[#C9A64A] mb-2">Company Name</label>
              <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-[#C9A64A] outline-none" />
            </div>
            <div className="bg-white/5 p-4 rounded-xl border border-white/10">
              <label className="block text-xs font-bold uppercase text-[#C9A64A] mb-2">Date</label>
              <input type="text" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-[#C9A64A] outline-none" />
            </div>
          </div>

          <div className="relative mb-4 sm:mb-6">
            <div className="bg-gradient-to-br from-[#0F1A2A] via-[#1A1A1C] to-[#0F1A2A] rounded-xl sm:rounded-2xl shadow-2xl border border-white/10 overflow-hidden" style={{ minHeight: '450px' }}>
              <div className="absolute top-3 left-4 text-[#C9A64A] text-xs font-bold tracking-wider uppercase z-10">{companyName}</div>
              <div className="absolute top-3 right-4 text-gray-400 text-xs font-semibold z-10">{currentSlide + 1}/{totalSlides}</div>
              <div className="pt-10 h-full">{renderSlide()}</div>
            </div>

            <div className="flex items-center justify-center gap-3 sm:gap-4 mt-4 sm:mt-6">
              <button onClick={prevSlide} disabled={currentSlide === 0} className="px-3 sm:px-5 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-semibold hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-sm">
                <ChevronLeft className="inline h-4 w-4" /> Prev
              </button>
              <div className="flex items-center gap-1 overflow-x-auto max-w-[150px] sm:max-w-none">
                {Array.from({ length: totalSlides }).map((_, i) => (
                  <button key={i} onClick={() => setCurrentSlide(i)} className={`w-2 h-2 rounded-full flex-shrink-0 transition-all ${i === currentSlide ? 'bg-[#C9A64A] w-4' : 'bg-white/20 hover:bg-white/40'}`} />
                ))}
              </div>
              <button onClick={nextSlide} disabled={currentSlide === totalSlides - 1} className="px-3 sm:px-5 py-2 bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] rounded-lg font-bold hover:scale-105 disabled:opacity-30 disabled:cursor-not-allowed text-sm">
                Next <ChevronRight className="inline h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
            <Download className="h-5 w-5 sm:h-6 sm:w-6 text-[#C9A64A]" /> Downloadable Resources
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {downloads.map((file) => (
              <div key={file.id} className="bg-white/5 p-4 rounded-xl border border-white/10 hover:border-[#C9A64A]/50 transition-all group">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#C9A64A] to-[#D4B76A] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <file.icon className="h-5 w-5 text-[#0F1A2A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-white truncate">{file.name}</h3>
                    <p className="text-xs text-gray-400">{file.type} • {file.size}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-300 mb-3">{file.description}</p>
                <Button className="w-full bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 font-semibold text-xs" size="sm">
                  <Download className="mr-2 h-3 w-3" /> Download
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
